import { Component, effect, inject } from '@angular/core';
import { TrainingBlock } from '../training-block/training-block';
import { InformationBlock } from '../information-block/information-block';
import { CreateContentService } from '../../services/create-content.service';
import { RecommendationBlock } from '../recommendation-block/recommendation-block';
import { PublicationBlock } from '../publication-block/publication-block';
import { FormGroup, FormControl, Validators, FormControlStatus } from '@angular/forms';
import { PlanningType } from '../../types/planning.type';
import { VisibilityType } from '../../types/visibility.type';
import { AudioMode } from '../../types/audio-mode.type';
import { Observable, Subscription } from 'rxjs';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { SportType } from '../../types/sport.type';
import { SectionModelType } from '../../types/section.type';
import { MediaBlockService } from '../../services/media-block-service';
import { sectionsDurationValidator } from '../../utils/sections-duration.validator';

@Component({
  selector: 'app-creation-coaching-form',
  imports: [TrainingBlock, InformationBlock, RecommendationBlock, PublicationBlock],
  templateUrl: './creation-coaching-form.html',
  styleUrl: './creation-coaching-form.scss',
})
export class CreationCoachingForm {
  createContentService: CreateContentService = inject(CreateContentService);
  private readonly mediaBlockService: MediaBlockService = inject(MediaBlockService);

  coachingForm: FormGroup = new FormGroup({
    mediaBlock: new FormGroup({
      videoFile: new FormControl<File | null>(null, Validators.required),
      thumbnailTimestamp: new FormControl<number | null>(null),
    }),
    trainingBlock: new FormGroup({
      sport: new FormControl<SportType | undefined>(undefined, Validators.required),
      workoutMode: new FormControl<WorkoutMode | undefined>(undefined, Validators.required),
      sections: new FormControl<SectionModelType[]>(
        [],
        [
          Validators.required,
          sectionsDurationValidator(() => this.mediaBlockService.videoFileData()?.duration),
        ],
      ),
    }),
    informationBlock: new FormGroup({
      title: new FormControl<string | undefined>(undefined, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(128),
      ]),
      description: new FormControl<string | undefined>(undefined, [Validators.maxLength(256)]),
      recordedWith: new FormControl<string | undefined>(undefined, [Validators.maxLength(256)]),
      languageId: new FormControl<number | null>(null, Validators.required),
      vehiculeId: new FormControl<number | null>(null, Validators.required),
    }),
    recommandationBlock: new FormGroup({
      audioMode: new FormControl<null | AudioMode>('none', Validators.required),
    }),
    publicationBlock: new FormGroup({
      visibility: new FormControl<null | VisibilityType>('public', Validators.required),
      planning: new FormControl<null | PlanningType>('immediate', Validators.required),
      planningDate: new FormControl<Date | null>(null),
      planningTime: new FormControl<string | null>(null),
    }),
  });
  subscriptions: Subscription[] = [];

  constructor() {
    // L'editeur d'intervalles ecrit dans le service : on recopie dans le formulaire pour la validation.
    effect(() => {
      const trainingBlock: FormGroup = this.trainingBlockFormGroup;
      trainingBlock.get('sport')!.setValue(this.createContentService.coachingSport());
      trainingBlock.get('workoutMode')!.setValue(this.createContentService.coachingWorkoutMode());
      trainingBlock.get('sections')!.setValue(this.createContentService.coachingSections());
    });

    // La duree attendue des intervalles est celle de la video : on revalide quand elle change.
    effect(() => {
      this.mediaBlockService.videoFileData();
      this.trainingBlockFormGroup.get('sections')!.updateValueAndValidity();
    });
  }

  get mediaBlockFormGroup(): FormGroup {
    return this.coachingForm.get('mediaBlock') as FormGroup;
  }

  get trainingBlockFormGroup(): FormGroup {
    return this.coachingForm.get('trainingBlock') as FormGroup;
  }

  get publicationBlockFormGroup(): FormGroup {
    return this.coachingForm.get('publicationBlock') as FormGroup;
  }

  get informationBlockFormGroup(): FormGroup {
    return this.coachingForm.get('informationBlock') as FormGroup;
  }

  get recommendationBlockFormGroup(): FormGroup {
    return this.coachingForm.get('recommandationBlock') as FormGroup;
  }

  markAllErrors(): void {
    this.coachingForm.markAllAsTouched();
  }

  canDeactivate(): Observable<boolean> {
    return this.createContentService.canDeactivate(this.coachingForm, 'coaching');
  }

  ngOnInit(): void {
    const localSave: string | null = localStorage.getItem('coaching_form');
    // `patchValue` : la sauvegarde ne contient pas `mediaBlock`.
    if (localSave) {
      this.coachingForm.patchValue(JSON.parse(localSave));
      const trainingBlock: FormGroup = this.trainingBlockFormGroup;
      this.createContentService.coachingSport.set(trainingBlock.get('sport')!.value ?? undefined);
      this.createContentService.coachingWorkoutMode.set(
        trainingBlock.get('workoutMode')!.value ?? undefined,
      );
    }
    this.createContentService.restoreForm(this.coachingForm, 'coaching');

    this.coachingForm.statusChanges.subscribe((status: FormControlStatus) => {
      if (status === 'VALID') {
        this.createContentService.coachingFormIsValid.set(true);
      } else {
        this.createContentService.coachingFormIsValid.set(false);
      }
    });

    this.subscriptions.push(
      this.coachingForm.get('informationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.title.set(values.title ?? undefined);
        this.createContentService.description.set(values.description ?? '');
        this.createContentService.vehiculeId.set(values.vehiculeId);
        this.createContentService.languageId.set(values.languageId ?? undefined);
        this.createContentService.recordedWith.set(values.recordedWith ?? '');
      }),
      this.coachingForm.get('publicationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.updatePublication(values);
      }),
    );

    const pubBlock = this.coachingForm.get('publicationBlock')!;
    this.createContentService.updatePublication(pubBlock.value);

    const recBlock = this.coachingForm.get('recommandationBlock')!;
    this.createContentService.audioMode.set(recBlock.get('audioMode')!.value);

    const infoBlock = this.coachingForm.get('informationBlock')!;
    this.createContentService.title.set(infoBlock.get('title')!.value ?? undefined);
    this.createContentService.description.set(infoBlock.get('description')!.value ?? '');
    this.createContentService.vehiculeId.set(infoBlock.get('vehiculeId')!.value);
    this.createContentService.languageId.set(infoBlock.get('languageId')!.value ?? undefined);
    this.createContentService.recordedWith.set(infoBlock.get('recordedWith')!.value ?? '');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }
}
